import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { identifyUser, trackEvent } from '../lib/analytics';
import { formatDateLong } from '../lib/dateFormat';
import { destinoDespuesDeEntrar } from '../lib/rutaPendiente';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// TANDA AS-4 — hermano de AuthCallback.js (Google), que queda intacto.
// Diferencia única con el molde: Apple no vuelve por query string. El retorno de Apple
// es un POST que aterriza en el trampolín del backend, y el trampolín rebota aquí con
// los datos en el FRAGMENTO (`#`) — un fragmento no viaja al servidor ni al CDN, así
// que el id_token no acaba en logs ni en referrers. De ahí en adelante, mismo guion:
// state de un solo uso, y con state válido se llama al backend.
export default function AppleAuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { handleAppleCallback } = useAuth();
  const { t } = useTranslation('auth');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const rawHash = window.location.hash || '';
    const params = new URLSearchParams(rawHash.startsWith('#') ? rawHash.slice(1) : rawHash);
    const idToken = params.get('id_token');
    const state = params.get('state');
    const userPayload = params.get('user'); // solo en la PRIMERA autorización
    // AS-6a: el authorization code, puro acarreo. No participa en la identidad (eso lo
    // decide el id_token verificado en el backend): el backend lo cambia con Apple por
    // el refresh token que tendrá que revocar el día que se borre la cuenta. Puede
    // faltar — si falta, el login corre igual y no se archiva nada.
    const code = params.get('code');

    // Anti-CSRF state: single-use — read it, delete it, then compare. Mismatch/absent
    // → abort WITHOUT calling the backend. El nonce se borra en la misma pasada: si
    // este retorno no sirve, el par entero se quema.
    const storedState = sessionStorage.getItem('apple_oauth_state');
    const storedNonce = sessionStorage.getItem('apple_oauth_nonce');
    sessionStorage.removeItem('apple_oauth_state');
    sessionStorage.removeItem('apple_oauth_nonce');

    if (!idToken || !state || !storedState || state !== storedState) {
      toast.error(t('apple_callback.toasts.invalid_response'));
      navigate('/login');
      return;
    }

    (async () => {
      try {
        const data = await handleAppleCallback(idToken, storedNonce, userPayload, code);
        identifyUser(data.user.id, { email: data.user.email, plan: data.user.plan, name: data.user.name });
        trackEvent('user_logged_in_apple', { plan: data.user.plan });
        // Reddit Pixel SignUp: solo cuando el backend reporta cuenta recién creada
        // (alta nueva por Apple o conversión de invitado). Mismo criterio que Google.
        if (data.is_new_account && window.rdt) window.rdt('track', 'SignUp');
        toast.success(data.user.name
          ? t('apple_callback.welcome_template', { name: data.user.name })
          : t('apple_callback.welcome_simple'));
        // post-t7-1 (E1): llega a donde iba (sobrevive la ida y vuelta en la misma pestana) o al Panel
        const destino = destinoDespuesDeEntrar();
        window.history.replaceState(null, '', destino);
        navigate(destino, { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail?.code === 'email_recently_deleted') {
          toast.error(t('blocked.recently_deleted', {
            date: formatDateLong(detail.blocked_until),
          }), { duration: 10000 });
        } else {
          toast.error((typeof detail === 'string' && detail) || t('apple_callback.toasts.apple_failed_fallback'));
        }
        navigate('/login');
      }
    })();
  }, [navigate, handleAppleCallback, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0e0e0e]">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00] mx-auto" />
        <p className="text-[#6a6a6a] text-sm">{t('apple_callback.loading')}</p>
      </div>
    </div>
  );
}
